import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { filter, takeUntil, tap } from 'rxjs/operators';

import * as rootReducer from 'app/core/store';
import * as fromDeliveryActions from 'app/core/store/actions/delivery.actions';
import * as fromAssetAction from 'app/core/store/actions/delivery-assets.actions';
import * as fromSelectors from 'app/core/store/selectors';
import * as fromDeliverySelectors from 'app/core/store/selectors/delivery.selectors';
import { Delivery } from 'app/core/models';
import { PlatformService } from 'app/core/services/platform.service';
import { selectDownloadConfig } from 'app/core/store/selectors/offline.selectors';
import { HeaderDeliveryConstant } from './header-delivery-view.constants';
import { ModalDeliveryShareComponent } from '../../modals/modal-delivery-share/modal-delivery-share.component';
import { ModalsService } from '../../modals/modals.service';
import { ConfigModalAnimation } from '../../../../animations/config-modal-animation';
import { PreferencesComponent } from '../preferences/preferences.component';

@Component({
  standalone: false,
  selector: 'pptl-header-delivery-view',
  templateUrl: './header-delivery-view.component.html',
  styleUrls: ['./header-delivery-view.component.scss', '../header.scss'],
})
export class HeaderDeliveryViewComponent implements OnDestroy, OnInit {
  buttons = HeaderDeliveryConstant.deliveryButtons;
  delivery: Delivery = <Delivery>{};
  asset$: Observable<any>;
  isPlayingAll = false;
  types = [];
  downloadConfig$: Observable<any>;

  private prevUrl: string;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private store: Store<rootReducer.State>,
    private router: Router,
    private platformService: PlatformService,
    private modalsService: ModalsService,
    private animation: ConfigModalAnimation,
  ) {
    this.store.pipe(select(fromSelectors.getPrevUrl), takeUntil(this.destroy$)).subscribe((url) => {
      this.prevUrl = url;
    });
    this.downloadConfig$ = this.store.pipe(select(selectDownloadConfig));
  }

  ngOnInit() {
    this.setDeliveryData();
    this.filterOfflineButtons();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  nativePlay(nativeEl) {
    if (nativeEl) {
      nativeEl.play().catch(() => {
        if (nativeEl.paused) {
          nativeEl.play();
        }
      });
    }
  }

  nativePause(nativeEl) {
    if (nativeEl) {
      nativeEl.pause();
    }
  }

  togglePlay(status: boolean = false): void {
    this.store.dispatch(
      fromAssetAction.TogglePlaying({
        payload: {
          isPlayingAll: this.isPlayingAll,
          play: status,
        },
      }),
    );
  }

  setDeliveryData(): void {
    this.store
      .pipe(select(fromDeliverySelectors.getDeliveryData), takeUntil(this.destroy$))
      .subscribe((delivery: Delivery) => {
        this.delivery = delivery;
      });
  }

  isDwnlBtnShowen(button: any): boolean {
    return (
      this.isDevicePlatform && this.delivery.deliveryHasOfflineAssets && button.id === 'offline'
    );
  }

  isBtnActive(buttonId: string = ''): boolean {
    return this.router.url.includes(buttonId);
  }

  onMenuButtonClick(buttonId: string, delivery: Delivery): void {
    if (buttonId === 'share') {
      this.showDeliveryShareModal();
    } else {
      delivery[buttonId] = !delivery[buttonId];
      if (buttonId === 'user') {
        this.showPreferencesModal();
      }
      this.store.dispatch(fromDeliveryActions.SetShareState({ payload: false }));
    }
  }

  onBackClick(): void {
    if ((!!this.prevUrl && this.prevUrl.includes('inbox')) || this.prevUrl.includes('offline')) {
      this.router.navigateByUrl(this.prevUrl);
    } else {
      this.router.navigateByUrl('/inbox/current');
    }
  }

  async showDeliveryShareModal() {
    this.modalsService.showModal({
      component: ModalDeliveryShareComponent,
      cssClass: 'modal-delivery-share',
      componentProps: { deliveryKeyCode: this.delivery.deliveryKeyCode },
    });
  }

  async showPreferencesModal() {
    this.modalsService.showModal({
      enterAnimation: this.animation.getAnimations().enterAnimation,
      leaveAnimation: this.animation.getAnimations().leaveAnimation,
      cssClass: 'modal-preferences',
      component: PreferencesComponent,
      componentProps: { isHomePage: false },
    });
  }

  get isDevicePlatform(): boolean {
    return this.platformService.isCordova() && this.platformService.isMobile();
  }

  getButtons() {
    if (this.delivery && !this.delivery.deliverySharable) {
      this.buttons = this.buttons.filter((button) => button.id !== 'share');
    }
    return this.buttons;
  }

  filterOfflineButtons(): void {
    this.store
      .pipe(
        select(fromSelectors.selectOfflineState),
        filter((isOffline) => isOffline),
        tap((isOffline: any) => {
          this.buttons = this.buttons.filter((button) => button.offline === isOffline);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}
